import { removeEmptyParams } from './misc';
const WooCommerceRestApi = require( '@carmineconversano/woocommerce-rest-api-fork' ).default;

const isServer = typeof window === 'undefined';

// Circuit breaker to prevent repeated API failures during build
let apiFailureCount = 0;
let lastFailureTime = 0;
const MAX_FAILURES = 3;
const FAILURE_WINDOW = 60000; // 1 minute

const isApiCircuitOpen = () => {
  const now = Date.now();
  if (now - lastFailureTime > FAILURE_WINDOW) {
    apiFailureCount = 0;
    return false;
  }
  return apiFailureCount >= MAX_FAILURES;
};

const recordApiFailure = () => {
  apiFailureCount++;
  lastFailureTime = Date.now();
};

const getApiClient = () => {
  if (isServer) {
    if (!process.env.NEXT_PUBLIC_WORDPRESS_SITE_URL || !process.env.WC_CONSUMER_KEY || !process.env.WC_CONSUMER_SECRET) {
      throw new Error('WooCommerce API credentials are not configured');
    }

    return new WooCommerceRestApi({
      url: process.env.NEXT_PUBLIC_WORDPRESS_SITE_URL,
      consumerKey: process.env.WC_CONSUMER_KEY,
      consumerSecret: process.env.WC_CONSUMER_SECRET,
      version: 'wc/v3',
    });
  }
  return null;
};

// GENERAL
/**
 * Get General Settings.
 *
 * @return {Promise<void>}
 */
export const getGeneralSettings = async () => {
  const api = getApiClient();
  const generalSettings = await api.get(
    'settings/general',
    {},
  );

  return generalSettings;
};

// PRODUCTS
/**
 * Get Products.
 *
 * @return {Promise<void>}
 */
export const getProductsData = async (perPage = 50, status = 'publish', featured = false, search = '', category = false, page = 1, include = [], orderby = 'date', order = 'desc', tag = false, on_sale = false) => {
  const api = getApiClient();
  let orderByInclude = false;
  if (orderby === 'manual') {
    orderByInclude = true;
    orderby = 'title';
  }
  const queryParams = {
    per_page: perPage,
    status: status,
    page: page,
    order: order,
    orderby: orderby,
  };

  if (search) queryParams.search = search;
  if (featured) queryParams.featured = featured;
  if (category) queryParams.category = category;

  // Handle tag or tag arrays
  if (tag) {
    if (Array.isArray(tag)) {
      // For multiple tags, WooCommerce expects comma-separated IDs
      queryParams.tag = tag.join(',');
    } else {
      queryParams.tag = tag;
    }
  }

  if (on_sale) queryParams.on_sale = on_sale;

  if (include.length > 0) {
    queryParams.include = include;
  }

  try {
    const response = await api.get('products', queryParams);
    const products = Array.isArray(response.data) ? response.data : [];
    const crosssellProducts = {};

    // Check for cross-sell products and fetch them
    const crossSellRequests = products
      .filter(product => product?.cross_sell_ids?.length > 0)
      .map(async (product) => {
        for (const crosssellId of product.cross_sell_ids) {
          // Check if cross-sell product is already in the product list
          const isAlreadyInList = products.find(p => p.id === crosssellId);

          if (isAlreadyInList && !crosssellProducts[crosssellId]) {
            crosssellProducts[crosssellId] = isAlreadyInList;
          } else if (!crosssellProducts[crosssellId]) {
            try {
              const { data: crosssellProduct } = await getProductByID(crosssellId);
              crosssellProducts[crosssellId] = crosssellProduct;
            } catch (error) {
              console.error(`Error fetching cross-sell product ${crosssellId}:`, error);
            }
          }
        }
      });

    // Await cross-sell requests
    if (crossSellRequests.length > 0) {
      await Promise.all(crossSellRequests);
    }

    if (include.length > 0 && orderByInclude) {
      // Sort products by include order from the include array ids
      products.sort((a, b) => {
        // Convert IDs to numbers to ensure consistent comparison
        const aId = parseInt(a.id, 10);
        const bId = parseInt(b.id, 10);
        const aIndex = include.indexOf(aId);
        const bIndex = include.indexOf(bId);

        // If both IDs are in the include array, sort by their position
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }

        // If only one ID is in the include array, prioritize it
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;

        // If neither ID is in the include array, maintain their original order
        return 0;
      });
    }

    return { data: products, crossSellProducts: Object.values(crosssellProducts) };
  } catch (error) {
    console.error('Error fetching products:', error);
    return { data: [], crossSellProducts: [] };
  }
};

/**
 * Get Variations for a Variable Product.
 *
 * @param {number} productId - ID of the product.
 * @return {Promise<void>}
 */
export const getProductVariations = async (productId) => {
  const api = getApiClient();
  const variations = await api.get(
    `products/${productId}/variations`,
    {
      per_page: 50, // Adjust as needed
    },
  );

  return variations;
};


/**
 * Get Product Settings.
 *
 * @return {Promise<void>}
 */
export const getProductsSettings = async () => {
  const api = getApiClient();
  const productSettings = await api.get(
    'settings/products',
    {},
  );

  return productSettings;
};

/**
 * Get Single Product By Slug.
 *
 * @return {Promise<void>}
 */
export const getProductBySlug = async ( productSlug = '' ) => {
  const api = getApiClient();
  return await api.get(
    'products',
    {
      slug: productSlug,
    },
  );
};

/**
 * Get Single Product By id.
 *
 * @return {Promise<void>}
 */
export const getProductByID = async ( productID = '' ) => {
  if (isServer) {
    try {
      const api = getApiClient();
      const response = await api.get(`products/${productID}`);
      return {
        status: 'success',
        data: response.data,
      };
    } catch (error) {
      return {
        status: error.response?.status === 404 ? 'not_found' : 'error',
        data: {
          status: 'private',
          purchasable: false,
        },
      };
    }
  } else {
    try {
      const response = await fetch(`/api/shop/product/${productID}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return {
        status: 'success',
        data: data,
      };
    } catch (error) {
      console.error('Error fetching product:', error);
      return {
        status: error.response?.status === 404 ? 'not_found' : 'error',
        data: {
          status: 'private',
          purchasable: false,
        },
      };
    }
  }
};

// ACCOUNT
/**
 * Get account information by id.
 *
 * @param {number} userId - ID of the customer.
 * @return {Promise<void>}
 */
export const getAccount = async (userId) => {
  const api = getApiClient();
  const account = await api.get(
    `customers/${userId}`,
  );

  return account;
};

/**
 * Update account information by id.
 *
 * @param {number} userId - ID of the customer.
 * @param {string} userData - Data of the customer.
 * @return {Promise<void>}
 */
export const verifyPassword = async (email, password) => {
  try {
    const response = await fetch(`${process.env.NEXT_PUBLIC_WORDPRESS_SITE_URL}/wp-json/jwt-auth/v1/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username: email,
        password: password,
      }),
    });

    const data = await response.json();
    return response.ok && data.token;
  } catch (error) {
    console.error('Error verifying password:', error);
    return false;
  }
};

export const createAccount = async (userData) => {
  const api = getApiClient();
  const account = await api.post(
    'customers',
    userData,
  );

  return account;
};

export const updateAccount = async (userId, userData) => {
  const api = getApiClient();
  const cleanUserData = removeEmptyParams(userData);
  const account = await api.put(
    `customers/${userId}`, cleanUserData,
  );

  return account;
};

/**
 * Get account orders by id.
 *
 * @param {number} userId - ID of the customer.
 * @return {Promise<void>}
 */
export const getUserOrders = async (userId) => {
  const api = getApiClient();
  const account = await api.get(
    `orders`, {customer: userId},
  );

  return account;
};

/**
 * Get all product categories.
 * @return {Promise<void>}
 */
export const getAllProductCategories = async () => {
  const api = getApiClient();
  try {
    const { data } = await withTimeoutAndRetry(() =>
      api.get('products/categories', { per_page: 100, orderby: 'term_group' }),
    );
    // Sort categories by menu_order manually
    const sortedCategories = data.sort((a, b) => a.menu_order - b.menu_order);

    return sortedCategories;
  } catch (error) {
    console.error('Error fetching product categories:', error);
    return [];
  }
};

// Helper function to add timeout and retry logic with circuit breaker
const withTimeoutAndRetry = async (apiCall, timeoutMs = 60000, maxRetries = 3) => {
  // Check circuit breaker first
  if (isApiCircuitOpen()) {
    console.warn('API circuit breaker is open, skipping API call');
    throw new Error('API circuit breaker is open due to repeated failures');
  }

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error(`Request timeout after ${timeoutMs}ms`)), timeoutMs);
      });

      const result = await Promise.race([apiCall(), timeoutPromise]);
      return result;
    } catch (error) {
      console.warn(`API call attempt ${attempt}/${maxRetries} failed:`, error.message);

      // Record failure for circuit breaker
      recordApiFailure();

      if (attempt === maxRetries) {
        throw error;
      }

      // Exponential backoff: wait longer between retries
      const delay = Math.min(1000 * Math.pow(2, attempt - 1), 10000);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

export const getSubcategoriesAndProducts = async (parentCategoryId, perPage = 10, page = 1) => {
  const api = getApiClient();
  try {
    // Check if circuit breaker is open and return minimal data
    if (isApiCircuitOpen()) {
      console.warn('API circuit breaker is open, returning minimal category structure');
      return [{
        subcategory: { id: parentCategoryId, name: 'Loading...' },
        fetchedProducts: [],
        fetchedCrossSellProducts: [],
      }];
    }

    // Fetch subcategories with timeout and retry
    const subcategories = await withTimeoutAndRetry(() =>
      api.get('products/categories', {
        parent: parentCategoryId,
        orderby: 'name',
        hide_empty: true, // Hide empty categories
      }),
    );

    // Sort categories by menu_order manually
    const sortedCategories = subcategories.data.sort((a, b) => a.menu_order - b.menu_order);

    // Fetch products for each subcategory with individual error handling
    const productRequests = sortedCategories.map(async (subcategory) => {
      try {
        const result = await withTimeoutAndRetry(() =>
          getProductsData(perPage, 'publish', false, '', subcategory.id, page, [], 'title', 'asc'),
        );
        return {
          subcategory,
          fetchedProducts: result.data,
          fetchedCrossSellProducts: result.crossSellProducts,
        };
      } catch (error) {
        console.warn(`Failed to fetch products for category ${subcategory.id} (${subcategory.name}):`, error.message);
        // Return empty arrays to prevent build failure
        return {
          subcategory,
          fetchedProducts: [],
          fetchedCrossSellProducts: [],
        };
      }
    });

    const subcategoriesWithProducts = await Promise.all(productRequests);
    return subcategoriesWithProducts;
  } catch (error) {
    console.error('Error fetching subcategories and products:', error);
    // Return minimal structure to prevent build failure
    return [{
      subcategory: { id: parentCategoryId, name: 'Loading...' },
      fetchedProducts: [],
      fetchedCrossSellProducts: [],
    }];
  }
};

/**
 * Update order by ID.
 *
 * @param {number} orderId - ID of the order to update.
 * @param {Object} orderData - Data to update in the order.
 * @return {Promise<Object>} Updated order data
 */
export const updateOrder = async (orderId, orderData) => {
  const api = getApiClient();
  try {
    const cleanOrderData = removeEmptyParams(orderData);
    const response = await api.put(
      `orders/${orderId}`,
      cleanOrderData,
    );
    return response.data;
  } catch (error) {
    console.error('Error updating order:', error);
    throw error;
  }
};

/**
 * Create a new order.
 *
 * @param {Object} orderData - Data for creating the order.
 * @return {Promise<Object>} Created order data
 */
export const createOrder = async (orderData) => {
  const api = getApiClient();
  try {
    const response = await api.post('orders', orderData);
    return response.data;
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
};

/**
 * Get order details by ID.
 *
 * @param {number} orderId - ID of the order to fetch.
 * @param {string} token - JWT token for authentication.
 * @return {Promise<Object>} Order data
 */
export const getOrderDetails = async (orderId) => {
  const api = getApiClient();
  try {
    const response = await api.get(`orders/${orderId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching order details:', error);
    throw error;
  }
};

/**
 * Get all product tags (server-side only).
 * @param {Object} options - { per_page, hide_empty }
 * @return {Promise<Array>} Array of tags
 */
export const getAllProductTags = async ({ per_page = 100, hide_empty = true } = {}) => {
  const api = getApiClient();
  try {
    const { data } = await api.get('products/tags', {
      per_page,
      hide_empty,
    });
    return { status: 'success', tags: data };
  } catch (error) {
    console.error('Error fetching product tags:', error);
    return { status: 'error', tags: [], error };
  }
};
